import React from 'react';
import { Building2, Calendar, CheckCircle2, Clock, Users, BarChart3, ExternalLink } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { formatDate } from '../../utils/dateUtils';

interface ProjectSummary {
  projectNumber: string;
  client: string;
  startDate?: string | Date;
  tasksTotal: number;
  tasksCompleted: number;
  tasksInProgress: number;
  totalHours: string | number;
  status: string;
}

interface ProjectSummaryCardProps {
  message: string;
  summary: ProjectSummary;
  projectId?: string;
  isDark?: boolean;
}

export const ProjectSummaryCard: React.FC<ProjectSummaryCardProps> = ({
  message,
  summary,
  projectId,
  isDark = false
}) => {
  const { t } = useTranslation();

  const tasksRemaining = summary.tasksTotal - summary.tasksCompleted;
  const completionPercentage = summary.tasksTotal > 0 
    ? Math.round((summary.tasksCompleted / summary.tasksTotal) * 100) 
    : 0;

  const handleViewProject = () => {
    if (projectId) {
      window.location.href = `/projects/${projectId}`;
    }
  };

  return (
    <div className={`rounded-lg border shadow-sm overflow-hidden ${
      isDark 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div className={`px-4 py-3 border-b ${
        isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      }`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-2 flex-1">
            <Building2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
              isDark ? 'text-accent-400' : 'text-accent-600'
            }`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-semibold ${
                isDark ? 'text-gray-100' : 'text-gray-900'
              }`}>
                {summary.projectNumber}
              </p>
              {message && (
                <p className={`text-xs mt-0.5 ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {message}
                </p>
              )}
            </div>
          </div>
          {projectId && (
            <button
              onClick={handleViewProject}
              className={`ml-2 p-1.5 rounded-md transition-colors ${
                isDark
                  ? 'text-gray-400 hover:text-accent-400 hover:bg-gray-700'
                  : 'text-gray-500 hover:text-accent-600 hover:bg-gray-100'
              }`}
              title={t('project.viewProject')}
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Client & Status */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <p className={`text-xs font-medium mb-1 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {t('project.client')}
            </p>
            <p className={`text-sm ${
              isDark ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {summary.client}
            </p>
          </div>
          <div>
            <p className={`text-xs font-medium mb-1 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {t('project.status')}
            </p>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
              summary.status === 'Active'
                ? isDark
                  ? 'bg-success-900/30 text-success-400 border border-green-800/50'
                  : 'bg-success-100 text-success-800 border border-green-200'
                : isDark
                  ? 'bg-gray-700 text-gray-400 border border-gray-600'
                  : 'bg-gray-100 text-gray-600 border border-gray-200'
            }`}>
              {summary.status}
            </span>
          </div>
        </div>

        {/* Start Date */}
        {summary.startDate && (
          <div>
            <p className={`text-xs font-medium mb-1 ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {t('project.startDate')}
            </p>
            <div className="flex items-center space-x-2">
              <Calendar className={`w-4 h-4 ${
                isDark ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <p className={`text-sm ${
                isDark ? 'text-gray-200' : 'text-gray-900'
              }`}>
                {formatDate(summary.startDate)}
              </p>
            </div>
          </div>
        )}

        {/* Tasks Progress */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className={`text-xs font-medium ${
              isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {t('project.tasks')}
            </p>
            <p className={`text-xs font-semibold ${
              isDark ? 'text-gray-300' : 'text-gray-700'
            }`}>
              {summary.tasksCompleted} / {summary.tasksTotal} {t('project.completed')}
            </p>
          </div>
          <div className={`h-2 rounded-full overflow-hidden ${
            isDark ? 'bg-gray-700' : 'bg-gray-200'
          }`}>
            <div
              className={`h-full transition-all ${
                isDark ? 'bg-accent-500' : 'bg-accent-600'
              }`}
              style={{ width: `${completionPercentage}%` }}
            />
          </div>
          <div className="flex items-center space-x-4 mt-2">
            <div className="flex items-center space-x-1">
              <CheckCircle2 className={`w-3 h-3 ${
                isDark ? 'text-success-400' : 'text-success-600'
              }`} />
              <span className={`text-xs ${
                isDark ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {summary.tasksCompleted} {t('project.completed')}
              </span>
            </div>
            {summary.tasksInProgress > 0 && (
              <div className="flex items-center space-x-1">
                <Clock className={`w-3 h-3 ${
                  isDark ? 'text-warning-400' : 'text-warning-600'
                }`} />
                <span className={`text-xs ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {summary.tasksInProgress} {t('project.inProgress')}
                </span>
              </div>
            )}
            {tasksRemaining > 0 && (
              <div className="flex items-center space-x-1">
                <BarChart3 className={`w-3 h-3 ${
                  isDark ? 'text-gray-500' : 'text-gray-400'
                }`} />
                <span className={`text-xs ${
                  isDark ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  {tasksRemaining} {t('project.remaining')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Total Hours */}
        <div className={`pt-3 border-t ${
          isDark ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className={`w-4 h-4 ${
                isDark ? 'text-gray-500' : 'text-gray-400'
              }`} />
              <p className={`text-xs font-medium ${
                isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {t('project.totalHours')}
              </p>
            </div>
            <p className={`text-sm font-semibold ${
              isDark ? 'text-gray-200' : 'text-gray-900'
            }`}>
              {typeof summary.totalHours === 'number' 
                ? summary.totalHours.toFixed(2) 
                : summary.totalHours} {t('project.hours')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
